import { apiRequest, apiRequestEnvelope } from '@/api/http/fetch'
import { getAccessToken } from '@/api/http/tokens'
import { buildQueryString } from '@/lib/helpers'

export interface DocumentResponse {
  id: number
  project_id: number
  folder_id?: number
  title: string
  description?: string
  owner_id: number
  owner_name?: string
  owner_avatar_url?: string
  owner_is_deleted?: boolean
  original_name?: string
  project_name?: string
  status: string
  current_version: number
  extracted_text?: string
  extraction_status?: string
  created_at: string
  updated_at: string
}

export interface PaginatedResponse<T> {
  items: T[]
  total: number
  page: number
  limit: number
}

export interface ListDocumentsParams {
  folder_id?: number
  page?: number
  limit?: number
  q?: string
  search_content?: boolean
  scope?: 'project' | 'folder'
  extensions?: string
  tags?: string
  status?: string
}

export async function listDocuments(
  projectId: number,
  params?: ListDocumentsParams,
): Promise<PaginatedResponse<DocumentResponse>> {
  const qs = buildQueryString(params as Record<string, string | number | undefined | null>)
  const body = await apiRequestEnvelope<DocumentResponse[]>(
    `/api/v1/projects/${projectId}/documents${qs}`,
  )
  return {
    items: body.data ?? [],
    total: body.meta?.total ?? 0,
    page: body.meta?.page ?? 1,
    limit: body.meta?.limit ?? 10,
  }
}

export interface ExtensionCount {
  extension: string
  count: number
}

export function getDocumentExtensions(projectId: number, folderId?: number): Promise<ExtensionCount[]> {
  return apiRequest<ExtensionCount[]>(
    `/api/v1/projects/${projectId}/document-extensions${folderId != null ? `?folder_id=${folderId}` : ''}`,
  )
}

export function getDocument(id: number): Promise<DocumentResponse> {
  return apiRequest<DocumentResponse>(`/api/v1/documents/${id}`)
}

export interface UpdateDocumentRequest {
  title?: string
  description?: string
  status?: string
  folder_id?: number | null
}

export function updateDocument(id: number, req: UpdateDocumentRequest): Promise<DocumentResponse> {
  return apiRequest<DocumentResponse>(`/api/v1/documents/${id}`, {
    method: 'PUT',
    body: JSON.stringify(req),
  })
}

export function moveDocument(id: number, folderId: number | null): Promise<void> {
  return apiRequest<void>(`/api/v1/documents/${id}/move`, {
    method: 'PUT',
    body: JSON.stringify({ folder_id: folderId }),
  })
}

export interface VersionResponse {
  id: number
  document_id: number
  version: number
  file_id: number
  original_name: string
  file_size: number
  mime_type: string
  change_note?: string
  created_by: number
  created_by_email?: string
  created_by_role?: string
  created_at: string
  parent_version?: number | null
  extracted_text?: string
  extraction_status?: string
  can_request_ocr?: boolean
}

export interface VersionDiffSummary {
  version: number
  file_id: number
  original_name: string
  file_size: number
  mime_type: string
  change_note?: string
  created_by: number
  created_by_email?: string
  created_by_role?: string
  created_at: string
  parent_version?: number | null
}

export interface VersionDiffResponse {
  v1: VersionDiffSummary
  v2: VersionDiffSummary
  meta_diff?: Record<string, { old: string; new: string }>
  text_diff?: {
    added_lines: number
    removed_lines: number
    hunks: { lines: { type: string; text: string }[] }[]
  }
}

export async function listVersions(docId: number): Promise<VersionResponse[]> {
  const body = await apiRequestEnvelope<VersionResponse[]>(`/api/v1/documents/${docId}/versions`)
  return body.data ?? []
}

export async function getVersionDiff(docId: number, v1: number, v2: number): Promise<VersionDiffResponse | null> {
  return apiRequest<VersionDiffResponse>(`/api/v1/documents/${docId}/diff?v1=${v1}&v2=${v2}`).catch(() => null)
}

export async function setCurrentVersion(docId: number, version: number): Promise<void> {
  return apiRequest<void>(`/api/v1/documents/${docId}/versions/${version}/set-current`, {
    method: 'POST',
  })
}

export interface SearchDocumentParams {
  project_id: number
  q?: string
  tags?: string
  page?: number
  limit?: number
}

export async function searchDocuments(params: SearchDocumentParams): Promise<PaginatedResponse<DocumentResponse>> {
  const qs = new URLSearchParams()
  qs.set('project_id', String(params.project_id))
  if (params.q) qs.set('q', params.q)
  if (params.tags) qs.set('tags', params.tags)
  if (params.page) qs.set('page', String(params.page))
  if (params.limit) qs.set('limit', String(params.limit))
  const body = await apiRequestEnvelope<DocumentResponse[]>(`/api/v1/documents/search?${qs.toString()}`)
  return {
    items: body.data ?? [],
    total: body.meta?.total ?? 0,
    page: body.meta?.page ?? 1,
    limit: body.meta?.limit ?? 10,
  }
}

export interface SearchAllDocumentsParams {
  q?: string
  search_content?: boolean
  tags?: string
  extensions?: string
  status?: string
  page?: number
  limit?: number
}

export async function searchAllDocuments(params?: SearchAllDocumentsParams): Promise<PaginatedResponse<DocumentResponse>> {
  const qs = buildQueryString(params as Record<string, string | number | boolean | undefined | null>)
  const body = await apiRequestEnvelope<DocumentResponse[]>(`/api/v1/documents/search${qs}`)
  return {
    items: body.data ?? [],
    total: body.meta?.total ?? 0,
    page: body.meta?.page ?? 1,
    limit: body.meta?.limit ?? 10,
  }
}

export function deleteVersion(docId: number, version: number): Promise<void> {
  return apiRequest<void>(`/api/v1/documents/${docId}/versions/${version}`, {
    method: 'DELETE',
  })
}

export function deleteVersionFile(docId: number, version: number): Promise<void> {
  return apiRequest<void>(`/api/v1/documents/${docId}/versions/${version}/file`, {
    method: 'DELETE',
  })
}

export function requestVersionOCR(docId: number, version: number): Promise<void> {
  return apiRequest<void>(`/api/v1/documents/${docId}/versions/${version}/ocr`, {
    method: 'POST',
  })
}

export function deleteFile(documentId: number, fileId: number): Promise<void> {
  return apiRequest<void>(`/api/v1/documents/${documentId}/files/${fileId}`, {
    method: 'DELETE',
  })
}

export interface DocumentTag {
  document_id: number
  tag: string
  created_at: string
}

export interface TagCount {
  tag: string
  count: number
}

export function addTag(docId: number, tag: string): Promise<void> {
  return apiRequest<void>(`/api/v1/documents/${docId}/tags`, {
    method: 'POST',
    body: JSON.stringify({ tag }),
  })
}

export function removeTag(docId: number, tag: string): Promise<void> {
  return apiRequest<void>(`/api/v1/documents/${docId}/tags?tag=${encodeURIComponent(tag)}`, {
    method: 'DELETE',
  })
}

export async function listTags(docId: number): Promise<DocumentTag[]> {
  const body = await apiRequestEnvelope<DocumentTag[]>(`/api/v1/documents/${docId}/tags`)
  return body.data ?? []
}

export async function getPopularTags(projectId: number, limit = 50): Promise<TagCount[]> {
  const body = await apiRequestEnvelope<TagCount[]>(`/api/v1/tags/popular?project_id=${projectId}&limit=${limit}`)
  return body.data ?? []
}

export function uploadVersion(
  docId: number,
  file: File,
  onProgress?: (pct: number) => void,
): Promise<{ version?: number; unchanged?: boolean; content_hash?: string }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    const form = new FormData()
    form.append('file', file)

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100))
      }
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const body = JSON.parse(xhr.responseText)
          resolve(body.data ?? body)
        } catch { reject(new Error('invalid response')) }
      } else {
        try {
          const body = JSON.parse(xhr.responseText)
          reject(new Error(body.error?.message ?? body.message ?? 'upload failed'))
        } catch { reject(new Error(`upload failed (${xhr.status})`)) }
      }
    }

    xhr.onerror = () => reject(new Error('network error'))
    xhr.onabort = () => reject(new Error('aborted'))

    xhr.open('POST', `/api/v1/documents/${docId}/upload`)
    const token = getAccessToken()
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    xhr.send(form)
  })
}

export function uploadDocument(
  projectId: number,
  file: File,
  folderId?: number,
  title?: string,
  onProgress?: (pct: number) => void,
  onXhr?: (xhr: XMLHttpRequest) => void,
): Promise<{ id: number; deduplicated?: boolean; content_hash?: string }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    const form = new FormData()
    form.append('project_id', String(projectId))
    form.append('file', file)
    if (folderId) form.append('folder_id', String(folderId))
    if (title) form.append('title', title)

    if (onXhr) onXhr(xhr)

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100))
      }
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const body = JSON.parse(xhr.responseText)
          resolve(body.data ?? body)
        } catch { reject(new Error('invalid response')) }
      } else {
        try {
          const body = JSON.parse(xhr.responseText)
          reject(new Error(body.error?.message ?? body.message ?? 'upload failed'))
        } catch { reject(new Error(`upload failed (${xhr.status})`)) }
      }
    }

    xhr.onerror = () => reject(new Error('network error'))
    xhr.onabort = () => reject(new Error('aborted'))

    xhr.open('POST', '/api/v1/documents/upload')
    const token = getAccessToken()
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    xhr.send(form)
  })
}

export async function addFavorite(projectId: number): Promise<void> {
  await apiRequest(`/api/v1/projects/${projectId}/favorite`, { method: 'POST' })
}

export async function removeFavorite(projectId: number): Promise<void> {
  await apiRequest(`/api/v1/projects/${projectId}/favorite`, { method: 'DELETE' })
}

export async function checkFavorite(projectId: number): Promise<boolean> {
  const body = await apiRequest<{ favorited: boolean }>(`/api/v1/projects/${projectId}/favorite`)
  return body.favorited ?? false
}

export async function listFavoriteIds(): Promise<number[]> {
  const body = await apiRequestEnvelope<number[]>(`/api/v1/projects/favorites`)
  return body.data ?? []
}
